import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API, authHeaders, jsonAuthHeaders } from "../services/config";
import "./PrestacionesPagos.css";
import Button from "./ui/Button";
import EmptyState from "./ui/EmptyState";

function todayLocalISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function statusFromItem(item) {
  const costo = parseFloat(item.costo) || 0;
  const abonado = parseFloat(item.abonado) || 0;
  if (abonado <= 0) return "pendiente";
  if (abonado < costo) return "parcial";
  return "pagada";
}

const STATUS_LABELS = {
  pendiente: "Pendiente",
  parcial: "Parcial",
  pagada: "Pagada",
};

export default function PrestacionesPagos({ open = true, paciente, pacienteId, onClose, onSaved, embedded = false }) {
  const id = pacienteId || paciente?.id;
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNuevaModal, setShowNuevaModal] = useState(false);
  const [fecha, setFecha] = useState(todayLocalISO());
  const [prestacion, setPrestacion] = useState("");
  const [costo, setCosto] = useState("");
  const [abonado, setAbonado] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [fechaDesde, setFechaDesde] = useState(todayLocalISO());
  const [fechaHasta, setFechaHasta] = useState(todayLocalISO());
  const [paymentEdit, setPaymentEdit] = useState(null);
  const [nuevoPago, setNuevoPago] = useState("");

  const fetchMovimientos = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/pacientes/${id}/prestaciones`, { headers: authHeaders() });
      const data = await response.json();
      if (response.ok && data.success) setMovimientos(data.data || []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if ((embedded || open) && id) fetchMovimientos();
  }, [embedded, open, id, fetchMovimientos]);

  useEffect(() => {
    const modalOpen = showNuevaModal || Boolean(paymentEdit);
    if (!modalOpen) return undefined;
    document.body.classList.add("pp-subview-modal-open");
    return () => document.body.classList.remove("pp-subview-modal-open");
  }, [showNuevaModal, paymentEdit]);

  const resetNuevaForm = () => {
    setFecha(todayLocalISO());
    setPrestacion("");
    setCosto("");
    setAbonado("");
  };

  const openNuevaModal = () => {
    resetNuevaForm();
    setShowNuevaModal(true);
  };

  const closeNuevaModal = () => {
    setShowNuevaModal(false);
    resetNuevaForm();
  };

  const handleAgregar = async () => {
    if (!fecha || !prestacion.trim()) {
      alert("Completá fecha y prestación");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API}/pacientes/${id}/prestaciones`, {
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({
          fecha,
          prestacion: prestacion.trim(),
          costo: parseFloat(costo || 0),
          abonado: parseFloat(abonado || 0),
        }),
      });
      if (response.ok) {
        closeNuevaModal();
        fetchMovimientos();
        if (typeof onSaved === "function") onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (itemId) => {
    if (!window.confirm("¿Eliminar prestación?")) return;
    const response = await fetch(`${API}/prestaciones/${itemId}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (response.ok) {
      setMovimientos((prev) => prev.filter((row) => row.id !== itemId));
      if (typeof onSaved === "function") onSaved();
    }
  };

  const handleRegistrarPago = async () => {
    if (!paymentEdit) return;
    const abonadoActual = parseFloat(paymentEdit.abonado) || 0;
    const adicional = parseFloat(nuevoPago || 0);
    if (adicional <= 0) return;

    const payload = {
      ...paymentEdit,
      abonado: abonadoActual + adicional,
    };

    const response = await fetch(`${API}/prestaciones/${paymentEdit.id}`, {
      method: "PUT",
      headers: jsonAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      setPaymentEdit(null);
      setNuevoPago("");
      fetchMovimientos();
      if (typeof onSaved === "function") onSaved();
    }
  };

  const movimientosFiltrados = useMemo(() => {
    let list = [...movimientos];
    list = list.filter((item) => {
      const status = statusFromItem(item);
      if (estadoFilter !== "todos" && status !== estadoFilter) return false;
      if (fechaDesde && item.fecha < fechaDesde) return false;
      if (fechaHasta && item.fecha > fechaHasta) return false;
      return true;
    });

    list.sort((a, b) => {
      if (a.fecha !== b.fecha) return a.fecha < b.fecha ? 1 : -1;
      return (b.id || 0) - (a.id || 0);
    });
    return list;
  }, [estadoFilter, fechaDesde, fechaHasta, movimientos]);

  const totales = useMemo(() => movimientosFiltrados.reduce((acc, item) => {
    const costoNum = parseFloat(item.costo) || 0;
    const abonadoNum = parseFloat(item.abonado) || 0;
    return {
      costo: acc.costo + costoNum,
      abonado: acc.abonado + abonadoNum,
      saldo: acc.saldo + (costoNum - abonadoNum),
    };
  }, { costo: 0, abonado: 0, saldo: 0 }), [movimientosFiltrados]);

  const renderHistorialItem = (item) => {
    const costoNum = parseFloat(item.costo) || 0;
    const abonadoNum = parseFloat(item.abonado) || 0;
    const saldo = costoNum - abonadoNum;
    const status = statusFromItem(item);

    return (
      <article key={item.id} className="pp-historial-card">
        <div className="pp-historial-card__head">
          <time className="pp-historial-card__fecha">{item.fecha}</time>
          <span className={`pp-estado-pill pp-estado-${status}`}>{STATUS_LABELS[status]}</span>
        </div>
        <h5 className="pp-historial-card__titulo">{item.prestacion}</h5>
        <div className="pp-historial-card__montos">
          <span>Costo <strong>${costoNum.toFixed(2)}</strong></span>
          <span>Abonado <strong>${abonadoNum.toFixed(2)}</strong></span>
          <span className={saldo > 0 ? "pp-saldo-rojo" : "pp-saldo-verde"}>
            Saldo <strong>${saldo.toFixed(2)}</strong>
          </span>
        </div>
        <div className="pp-historial-card__acciones">
          <button type="button" className="boton-secundario" onClick={() => setPaymentEdit(item)}>
            Registrar pago
          </button>
          <Button variant="danger" onClick={() => handleEliminar(item.id)}>Eliminar</Button>
        </div>
      </article>
    );
  };

  if (!embedded && (!open || !paciente)) return null;

  return (
    <div className={embedded ? "pp-embedded pp-prestaciones-root" : "pp-overlay"} role={embedded ? undefined : "dialog"}>
      <div className="pp-prestaciones-panel">
        {!embedded && (
          <div className="pp-header">
            <h3>
              Prestaciones y Pagos{" "}
              <span>{paciente ? `${paciente.nombre} ${paciente.apellido}` : ""}</span>
            </h3>
            <button type="button" className="pp-volver" onClick={onClose}>← Volver</button>
          </div>
        )}

        <div className="pp-prestaciones-body">
          <button type="button" className="pp-btn-nueva-prestacion" onClick={openNuevaModal}>
            + Nueva prestación
          </button>

          <div className="pp-prest-filtros-compact">
            <select
              className="pp-filtro-input"
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
              aria-label="Filtrar por estado"
            >
              <option value="todos">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="parcial">Parcial</option>
              <option value="pagada">Pagada</option>
            </select>
            <input
              type="date"
              className="pp-filtro-input"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              aria-label="Desde"
            />
            <input
              type="date"
              className="pp-filtro-input"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              aria-label="Hasta"
            />
          </div>

          <div className="pp-prest-resumen">
            <span>Costo <strong>${totales.costo.toFixed(2)}</strong></span>
            <span>Abonado <strong>${totales.abonado.toFixed(2)}</strong></span>
            <span className={totales.saldo > 0 ? "pp-balance-neg" : "pp-balance-pos"}>
              Saldo <strong>${Math.abs(totales.saldo).toFixed(2)}</strong>
            </span>
          </div>

          <h4 className="pp-prest-historial-title">Historial</h4>

          {loading ? (
            <p className="pp-prest-loading">Cargando...</p>
          ) : movimientosFiltrados.length === 0 ? (
            <EmptyState title="Sin prestaciones" description="No hay registros para los filtros seleccionados." />
          ) : (
            <>
              <div className="pp-historial-cards">
                {movimientosFiltrados.map(renderHistorialItem)}
              </div>
              <div className="pp-tabla-wrapper pp-historial-table">
                <table className="pp-tabla">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Prestación</th>
                      <th>Costo</th>
                      <th>Abonado</th>
                      <th>Saldo</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosFiltrados.map((item) => {
                      const costoNum = parseFloat(item.costo) || 0;
                      const abonadoNum = parseFloat(item.abonado) || 0;
                      const saldo = costoNum - abonadoNum;
                      const status = statusFromItem(item);
                      return (
                        <tr key={item.id}>
                          <td>{item.fecha}</td>
                          <td>{item.prestacion}</td>
                          <td>${costoNum.toFixed(2)}</td>
                          <td>${abonadoNum.toFixed(2)}</td>
                          <td className={saldo > 0 ? "pp-saldo-rojo" : "pp-saldo-verde"}>${saldo.toFixed(2)}</td>
                          <td>
                            <span className={`pp-estado-pill pp-estado-${status}`}>{STATUS_LABELS[status]}</span>
                          </td>
                          <td className="pp-acciones-row">
                            <button type="button" className="boton-secundario" onClick={() => setPaymentEdit(item)}>
                              Registrar pago
                            </button>
                            <Button variant="danger" onClick={() => handleEliminar(item.id)}>Eliminar</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {showNuevaModal && (
          <div className="modal-historia pp-prest-nueva-overlay" role="presentation" onClick={closeNuevaModal}>
            <div
              className="modal-historia-content pp-prest-nueva-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pp-nueva-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="pp-nueva-title">Nueva prestación</h2>
              <div className="pp-prest-nueva-grid">
                <div className="pp-field">
                  <label htmlFor="pp-nueva-fecha">Fecha</label>
                  <input
                    id="pp-nueva-fecha"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                </div>
                <div className="pp-field pp-field--full">
                  <label htmlFor="pp-nueva-prestacion">Prestación</label>
                  <input
                    id="pp-nueva-prestacion"
                    type="text"
                    value={prestacion}
                    onChange={(e) => setPrestacion(e.target.value)}
                    placeholder="Ej: Limpieza, Endodoncia..."
                  />
                </div>
                <div className="pp-field">
                  <label htmlFor="pp-nueva-costo">Costo ($)</label>
                  <input
                    id="pp-nueva-costo"
                    type="number"
                    min="0"
                    step="0.01"
                    value={costo}
                    onChange={(e) => setCosto(e.target.value)}
                  />
                </div>
                <div className="pp-field">
                  <label htmlFor="pp-nueva-abonado">Abonado inicial ($)</label>
                  <input
                    id="pp-nueva-abonado"
                    type="number"
                    min="0"
                    step="0.01"
                    value={abonado}
                    onChange={(e) => setAbonado(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-historia-buttons">
                <button type="button" className="btn-cerrar" onClick={closeNuevaModal}>Cancelar</button>
                <button type="button" className="btn-guardar" onClick={handleAgregar} disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {paymentEdit && (
          <div className="modal-historia pp-prest-nueva-overlay" role="presentation" onClick={() => setPaymentEdit(null)}>
            <div
              className="modal-historia-content pp-prest-nueva-modal"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>Registrar pago</h2>
              <p className="pp-info pp-prest-pago-info">{paymentEdit.prestacion}</p>
              <p>Abonado actual: ${parseFloat(paymentEdit.abonado || 0).toFixed(2)}</p>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Monto adicional"
                value={nuevoPago}
                onChange={(e) => setNuevoPago(e.target.value)}
                className="pp-prest-input-full"
              />
              <div className="modal-historia-buttons">
                <button type="button" className="btn-cerrar" onClick={() => setPaymentEdit(null)}>Cancelar</button>
                <button type="button" className="btn-guardar" onClick={handleRegistrarPago}>Guardar pago</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
